import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreVertical,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Video,
  Rocket,
} from "lucide-react";
import {
  getNextClassDateTime,
  isClassActive,
  getJoinLink,
  calculateAgeFromDob,
  formatClassScheduleString,
} from "../utils/classScheduleUtils";
import { AI_LAB_COURSES } from "../../constants/ai-lab-constants";

const ChildCard = ({
  student,
  batches = [],
  onRemoveChild,
  onEditChild,
  onBookDemo,
  onOpenWorkspace,
}) => {
  const navigate = useNavigate();
  const { name, imageUrl, grade, school, dob, demo, demoBooking } = student;
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Check enrollment status
  const aiLabEnrolment = student.enrolment?.["ai-lab"];
  const isEnrolled = Boolean(
    (aiLabEnrolment && aiLabEnrolment.paymentStatus === "verified") ||
    (student.enrolments && Object.values(student.enrolments).some(e => e.paymentStatus === "verified")) ||
    student.status === "enrolled" ||
    student.isEnrolled
  );

  // Check demo booking status
  const demoData = demoBooking || demo || student.demoSchedule || null;
  const isDemoScheduled = Boolean(
    !isEnrolled && (
      demoData ||
      student.hasBookedDemo ||
      student.demoStatus === "scheduled" ||
      student.demoStatus === "pending" ||
      student.status === "demo_scheduled"
    )
  );

  // Generate initials for avatar placeholder
  const getInitials = (fullName) => {
    if (!fullName) return "?";
    return fullName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formattedGrade = grade
    ? grade.toLowerCase().startsWith("class")
      ? grade
      : `Class ${grade}`
    : "Class N/A";

  // Calculate age from DOB if present
  const calculatedAge = calculateAgeFromDob(dob || student.dateOfBirth);

  // Subtitle: Show Age if DOB is present, otherwise only Grade
  const ageGradeSubtitle = calculatedAge !== null
    ? `Age ${calculatedAge} • ${formattedGrade}`
    : formattedGrade;

  // Determine enrolled course details & next class
  let enrolledCourseName = "AI & Robotics - Level 2";
  let nextClassScheduleText = "Today, 4:00 PM";
  let activeClassMeetLink = "";
  let isClassCurrentlyActive = false;

  if (aiLabEnrolment && aiLabEnrolment.courses) {
    const courseEntries = Object.entries(aiLabEnrolment.courses);
    if (courseEntries.length > 0) {
      const [firstCourseId, sched] = courseEntries[0];
      const courseObj = AI_LAB_COURSES.find((c) => c.id === firstCourseId);
      if (courseObj) {
        enrolledCourseName = courseObj.label;
      }

      if (sched?.day && sched?.timeSlot) {
        const nextDate = getNextClassDateTime(sched.day, sched.timeSlot);
        nextClassScheduleText = formatClassScheduleString(sched.day, sched.timeSlot, nextDate);
        isClassCurrentlyActive = isClassActive(sched.day, sched.timeSlot);
        activeClassMeetLink = getJoinLink(name, firstCourseId, sched.day, sched.timeSlot, batches);
      }
    }
  }

  // Demo details
  const demoTopicName = demoData?.courseName || demoData?.topic || "AI & Robotics Intro";
  const demoScheduleText = demoData?.date
    ? `${demoData.date}${demoData.time ? `, ${demoData.time}` : ""}`
    : "Aug 25, 11:00 AM";

  const handleLaunchWorkspace = (e) => {
    e.stopPropagation();
    const childId = student.id || student.uid;
    if (onOpenWorkspace) {
      onOpenWorkspace(student);
    } else {
      navigate(`/dashboard/code-workspace${childId ? `?childId=${childId}` : ""}`);
    }
  };

  const handleRescheduleDemo = () => {
    if (onBookDemo) {
      onBookDemo(student);
    } else {
      navigate("/dashboard?type=book-robotics-lab-demo");
    }
  };

  // Avatar styling matching Image 3 (Blue for enrolled, Amber for demo scheduled)
  const avatarBgClass = isEnrolled
    ? "bg-blue-100 text-blue-700"
    : isDemoScheduled
    ? "bg-amber-100 text-amber-700"
    : "bg-blue-100 text-blue-700";

  return (
    <div className="bg-white border-2 border-indigo-400/90 hover:border-indigo-600 rounded-2xl p-4 sm:p-4.5 shadow-2xs hover:shadow-xs transition-all duration-200 flex flex-col justify-between space-y-3.5 text-left relative">
      {/* Top Row: Avatar + Info + Badge + 3-Dot Menu */}
      <div className="flex items-start justify-between gap-3">
        {/* Left: Avatar + Details */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              className="w-10 h-10 rounded-full object-cover border border-slate-100 shadow-3xs flex-shrink-0"
            />
          ) : (
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm shadow-3xs flex-shrink-0 ${avatarBgClass}`}
            >
              {getInitials(name)}
            </div>
          )}

          <div className="space-y-0.5 min-w-0 flex-1">
            <h4 className="text-sm sm:text-base font-extrabold text-slate-900 capitalize truncate leading-tight">
              {name}
            </h4>
            <p className="text-xs text-slate-500 font-medium truncate leading-tight">
              {ageGradeSubtitle}
            </p>
            <p className="text-[11px] text-slate-400 font-normal truncate leading-tight">
              {school || "Delhi Public School"}
            </p>
          </div>
        </div>

        {/* Right: Status Badge & 3-Dot Menu */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isEnrolled ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200/60">
              Enrolled
            </span>
          ) : isDemoScheduled ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/60">
              Demo Scheduled
            </span>
          ) : null}

          {/* 3-Dot Menu */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setShowMenu((prev) => !prev)}
              className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition cursor-pointer"
              title="Options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-7 z-30 w-40 bg-white rounded-xl shadow-xl border border-slate-150 p-1 text-left animate-fade-in space-y-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    if (onEditChild) {
                      onEditChild(student);
                    }
                  }}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition cursor-pointer"
                  title="Edit Child Details"
                >
                  <Pencil className="w-3.5 h-3.5 flex-shrink-0 text-purple-600" />
                  <span>Edit Details</span>
                </button>

                <button
                  type="button"
                  disabled={isEnrolled}
                  onClick={() => {
                    setShowMenu(false);
                    if (!isEnrolled && onRemoveChild) {
                      onRemoveChild(student);
                    }
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-bold rounded-lg transition ${
                    isEnrolled
                      ? "text-slate-300 cursor-not-allowed bg-slate-50"
                      : "text-red-600 hover:bg-red-50 cursor-pointer"
                  }`}
                  title={isEnrolled ? "Cannot remove student enrolled in courses" : "Remove child"}
                >
                  <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <div className="flex flex-col">
                    <span>Remove Child</span>
                    {isEnrolled && (
                      <span className="text-[9px] text-slate-400 font-normal">Enrolled in courses</span>
                    )}
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Middle Content Box: Course / Demo / Topic Info */}
      <div className="space-y-0.5 pt-0.5">
        {isEnrolled ? (
          <>
            <p className="text-[11px] text-slate-400 font-medium">
              Current Course
            </p>
            <h5 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
              {enrolledCourseName}
            </h5>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 pt-0.5">
              <Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span>Next class: {nextClassScheduleText}</span>
            </div>
          </>
        ) : isDemoScheduled ? (
          <>
            <p className="text-[11px] text-slate-400 font-medium">
              Selected Topic
            </p>
            <h5 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
              {demoTopicName}
            </h5>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 pt-0.5">
              <Calendar className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <span>Demo: {demoScheduleText}</span>
            </div>
          </>
        ) : (
          <>
            <p className="text-[11px] text-slate-400 font-medium">
              Selected Topic
            </p>
            <h5 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
              AI &amp; Robotics Intro
            </h5>
            <p className="text-xs text-slate-500 font-medium pt-0.5">
              Explore live interactive sessions in robotics and coding.
            </p>
          </>
        )}
      </div>

      {/* Buttons: 2 rows */}
      <div className="space-y-2.5 pt-1">
        {/* Row 1: 2 buttons */}
        <div className="flex items-center gap-2.5">
          {/* Button 1: View Details (Always Present) */}
          <button
            type="button"
            className="flex-1 py-2 px-3 rounded-xl border border-slate-250 hover:border-slate-350 hover:bg-slate-50 text-slate-700 font-bold text-xs text-center transition cursor-pointer whitespace-nowrap"
            title="Child Profile (Coming Soon)"
          >
            View Details
          </button>

          {/* Button 2: Join Class (if enrolled) OR Reschedule (if booked demo) OR Book Free Demo (if not enrolled) */}
          {isEnrolled ? (
            isClassCurrentlyActive ? (
              <a
                href={activeClassMeetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2 px-3 rounded-xl bg-[#5468ff] hover:bg-[#4355f0] text-white font-bold text-xs text-center shadow-xs flex items-center justify-center gap-1.5 transition cursor-pointer whitespace-nowrap"
              >
                <Video className="w-3.5 h-3.5 animate-bounce" />
                <span>Join Class</span>
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="Class link will become active 15 minutes before the scheduled time"
                className="flex-1 py-2 px-3 rounded-xl bg-slate-100 text-slate-400 font-bold text-xs text-center flex items-center justify-center gap-1.5 cursor-not-allowed whitespace-nowrap"
              >
                <Clock className="w-3.5 h-3.5 text-slate-350" />
                <span>Join Class</span>
              </button>
            )
          ) : isDemoScheduled ? (
            <button
              type="button"
              onClick={handleRescheduleDemo}
              className="flex-1 py-2 px-3 rounded-xl border border-slate-250 hover:border-slate-350 hover:bg-slate-50 text-slate-700 font-bold text-xs text-center transition cursor-pointer whitespace-nowrap"
            >
              Reschedule
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onBookDemo && onBookDemo(student)}
              className="flex-1 py-2 px-3 rounded-xl bg-[#5468ff] hover:bg-[#4355f0] text-white font-bold text-xs text-center shadow-xs transition cursor-pointer whitespace-nowrap"
            >
              Book Free Demo
            </button>
          )}
        </div>

        {/* Row 2: 1 button - Open Studio (For ALL children) */}
        <button
          type="button"
          onClick={handleLaunchWorkspace}
          className="w-full py-2 px-3 rounded-xl bg-gradient-to-r from-[#b5179e] to-[#7209b7] hover:from-[#a0138b] hover:to-[#5e0797] active:scale-99 text-white font-extrabold text-xs flex items-center justify-center gap-2 shadow-xs hover:shadow transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <Rocket className="w-3.5 h-3.5" />
          <span>Open Studio &rarr;</span>
        </button>
      </div>
    </div>
  );
};

export default ChildCard;
